import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { Check, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { WELCOME_TEMPLATES, fillWelcomeTemplate } from '../lib/welcomeTemplates';
import { barberRoleOptions, barberRoleLabel } from '../lib/barberRoles';

/**
 * /welcome — first-run wizard for a shop_admin who was created by an
 * admin via Flow 3 (or later Flow 2). Gated by shops.onboarding_completed_at
 * being NULL. Only "Profile" is required; Services / Stripe / Barber are
 * skippable.
 */

const PRESET_COLOURS = ['#1f2a1e', '#b03a2e', '#4f46ff', '#c8a35a', '#111210'];
const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])?$/;

const STEPS = [
  { key: 'profile',  label: 'Shop profile', required: true  },
  { key: 'services', label: 'Services',     required: false },
  { key: 'stripe',   label: 'Payments',     required: false },
  { key: 'barber',   label: 'First barber', required: false },
  { key: 'done',     label: 'Done',         required: false },
];

const emptyService = () => ({ name: '', price: '', duration_min: 30 });

const WelcomeInner = () => {
  const { profile } = useShell();
  const navigate = useNavigate();
  const shopId = profile?.shop_id;

  const [step, setStep] = useState(0);
  const [shop, setShop] = useState(null);           // current shops row
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Original subdomain snapshot — stable across re-renders so the
  // "exclude own subdomain" guard works even while the input mutates.
  const originalSubRef = useRef('');

  // Subdomain uniqueness (excluding own shop). Seeded to 'ok' at load
  // when the original subdomain is valid — otherwise the Profile step's
  // Next button is blocked until the owner edits the subdomain.
  const [subStatus, setSubStatus] = useState('idle');

  // Services state (added inline)
  const [services, setServices] = useState([]);
  // Barber state (single, inline)
  const [barberName, setBarberName] = useState('');
  const [barberRole, setBarberRole] = useState('');
  const [ownAsBarber, setOwnAsBarber] = useState(true); // default ON — most solo shops are owner-barbers
  const [ownerAuthUid, setOwnerAuthUid] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setOwnerAuthUid(user.id);
      setOwnerEmail(user.email || '');
      const { data: prof } = await supabase.from('user_profiles').select('full_name').eq('id', user.id).maybeSingle();
      const n = prof?.full_name || '';
      setOwnerName(n);
      if (!barberName) setBarberName(n);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shopId) return;
    (async () => {
      const { data } = await supabase.from('shops').select('*').eq('id', shopId).maybeSingle();
      if (data?.onboarding_completed_at) {
        // Already finished — redirect out
        navigate('/dashboard', { replace: true });
        return;
      }
      setShop(data);
      const orig = (data?.subdomain || '').toLowerCase();
      originalSubRef.current = orig;
      if (orig && SUBDOMAIN_RE.test(orig)) setSubStatus('ok');
      setLoading(false);
    })();
  }, [shopId, navigate]);

  const checkSubdomain = async (raw) => {
    const s = (raw || '').trim().toLowerCase();
    if (!s) { setSubStatus('idle'); return; }
    if (!SUBDOMAIN_RE.test(s)) { setSubStatus('invalid'); return; }
    // Compare against the stable original (not the mutating shop state)
    if (s === originalSubRef.current) { setSubStatus('ok'); return; }
    setSubStatus('checking');
    const { data } = await supabase.from('shops').select('id').eq('subdomain', s).limit(1);
    setSubStatus((data && data.length) ? 'taken' : 'ok');
  };

  const profileValid = useMemo(() => {
    if (!shop) return false;
    return (shop.name || '').trim().length >= 2 && subStatus === 'ok';
  }, [shop, subStatus]);

  const active = STEPS[step].key;

  // Save the current step's data before moving Next (or before finishing).
  const saveStep = async () => {
    setSaving(true); setError('');
    try {
      if (active === 'profile') {
        const { error: e1 } = await supabase.from('shops').update({
          name: (shop.name || '').trim(),
          sms_display_name: (shop.sms_display_name || '').trim() || null,
          subdomain: (shop.subdomain || '').trim().toLowerCase(),
          welcome_text: shop.welcome_text || '',
          accent_colour: shop.accent_colour || '#4f46ff',
        }).eq('id', shopId);
        if (e1) throw e1;
      } else if (active === 'services') {
        const rows = services
          .filter(s => s.name.trim().length >= 2 && Number(s.price) >= 0 && Number(s.duration_min) > 0)
          .map((s, i) => ({
            shop_id: shopId,
            name: s.name.trim(),
            price: Number(s.price),
            duration_min: Number(s.duration_min),
            sort_order: i,
          }));
        if (rows.length) {
          const { error: e2 } = await supabase.from('services').insert(rows);
          if (e2) throw e2;
        }
      } else if (active === 'barber') {
        // Seed 7 barber_hours rows for every barber created here.
        // Platform rule: a barber always has exactly 7 rows. Closed
        // day = is_open=false, never a missing row (avoids the inert-
        // toggle bug on the Barbers page's Weekly Hours panel).
        const seedHours = (barberId) => supabase.from('barber_hours').insert(
          [0, 1, 2, 3, 4, 5, 6].map(wd => ({
            barber_id: barberId,
            weekday: wd,
            start_time: '09:00',
            end_time: '18:00',
            is_open: wd !== 0,   // closed Sunday by default
          }))
        );
        // If "Add me as a barber" is checked, always insert an owner-linked row.
        // Otherwise use the manual name entry (skip if empty).
        if (ownAsBarber && ownerAuthUid) {
          const finalName = (barberName.trim() || ownerName || 'Owner').trim();
          const { data: bRow, error: e3 } = await supabase.from('barbers').insert([{
            shop_id: shopId,
            name: finalName,
            role: barberRole.trim() || null,
            email: ownerEmail || null,
            user_id: ownerAuthUid,
            active: true,
          }]).select().single();
          if (e3) throw e3;
          const { error: hrErr } = await seedHours(bRow.id);
          if (hrErr) console.warn('Seeding default barber_hours failed:', hrErr.message);
        } else if (barberName.trim().length >= 2) {
          const { data: bRow, error: e3 } = await supabase.from('barbers').insert([{
            shop_id: shopId,
            name: barberName.trim(),
            role: barberRole.trim() || null,
            active: true,
          }]).select().single();
          if (e3) throw e3;
          const { error: hrErr } = await seedHours(bRow.id);
          if (hrErr) console.warn('Seeding default barber_hours failed:', hrErr.message);
        }
      }
      // 'stripe' step has no data to persist — the button below hands off to /stripe-setup.
      return true;
    } catch (e) {
      setError(e.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const skip = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));
  const next = async () => {
    const ok = await saveStep();
    if (ok) setStep(s => Math.min(STEPS.length - 1, s + 1));
  };
  const canJumpTo = (i) => i <= step;

  const finish = async () => {
    setSaving(true); setError('');
    try {
      const { error: e } = await supabase.from('shops').update({
        onboarding_completed_at: new Date().toISOString(),
      }).eq('id', shopId);
      if (e) throw e;
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError(e.message || 'Finish failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="main">
        <div className="topbar"><div><h2>Loading…</h2></div></div>
      </main>
    );
  }

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Welcome to Instacut</h2>
          <div className="sub">Let&apos;s finish setting up your shop.</div>
        </div>
      </div>
      <div className="body">
        <div className="wiz admin-page" data-testid="welcome-wizard">
          <ul className="wiz-rail" data-testid="welcome-rail">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={i === step ? 'on' : (i < step ? 'done' : '')}
                onClick={() => canJumpTo(i) && setStep(i)}
                data-testid={`welcome-step-${s.key}`}
              >
                <span className="n">{i < step ? <Check size={12} /> : i + 1}</span>
                {s.label}{s.required ? '' : <small style={{ marginLeft: 6, color: 'var(--mute)' }}>(optional)</small>}
              </li>
            ))}
          </ul>
          <div className="panel" style={{ padding: 24 }}>
            {active === 'profile' && (
              <>
                <h3 style={{ marginTop: 0 }}>Shop profile</h3>
                <div className="wiz-row">
                  <div className="field">
                    <label>Shop name</label>
                    <input className="inp" value={shop.name || ''} onChange={(e) => setShop({ ...shop, name: e.target.value })} data-testid="welcome-shop-name" />
                  </div>
                  <div className="field">
                    <label>Subdomain <small style={{ color: 'var(--mute)' }}>.instacut.co.uk</small></label>
                    <input
                      className="inp"
                      value={shop.subdomain || ''}
                      onChange={(e) => {
                        const v = e.target.value.toLowerCase();
                        setShop({ ...shop, subdomain: v });
                        checkSubdomain(v);
                      }}
                      data-testid="welcome-shop-subdomain"
                    />
                    <SubStatus status={subStatus} />
                  </div>
                </div>
                <div className="field">
                  <label>
                    Shop SMS nickname <small style={{ color: 'var(--mute)' }}>{(shop.sms_display_name || '').length}/22 — leave blank to disable SMS</small>
                  </label>
                  <input
                    className="inp"
                    value={shop.sms_display_name || ''}
                    onChange={(e) => setShop({ ...shop, sms_display_name: e.target.value })}
                    placeholder="e.g. Sharp & Sons"
                    maxLength={22}
                    data-testid="welcome-shop-sms-nickname"
                  />
                </div>
                <div className="field">
                  <label>Welcome text (shows on the booking page)</label>
                  <select
                    className="inp"
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setShop({ ...shop, welcome_text: fillWelcomeTemplate(e.target.value, shop.name) });
                    }}
                    style={{ marginBottom: 8 }}
                    data-testid="welcome-shop-welcome-template"
                    aria-label="Pick a default welcome message"
                  >
                    <option value="">Use a default message…</option>
                    {WELCOME_TEMPLATES.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  <textarea className="inp" rows={3} value={shop.welcome_text || ''} onChange={(e) => setShop({ ...shop, welcome_text: e.target.value })} placeholder="Skin fades, hot towels, classic cuts." data-testid="welcome-shop-welcome" />
                </div>
                <div className="field">
                  <label>Accent colour</label>
                  <div className="swatches" data-testid="welcome-shop-accent">
                    {PRESET_COLOURS.map(c => (
                      <button
                        key={c}
                        type="button"
                        className={`sw ${shop.accent_colour === c ? 'on' : ''}`}
                        style={{ background: c }}
                        onClick={() => setShop({ ...shop, accent_colour: c })}
                        aria-label={c}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {active === 'services' && (
              <ServicesStep services={services} setServices={setServices} />
            )}

            {active === 'stripe' && (
              <>
                <h3 style={{ marginTop: 0 }}>Payments (Stripe)</h3>
                <div className="sub" style={{ color: 'var(--mute)', margin: '4px 0 14px' }}>
                  Connect Stripe now to collect deposits, or skip for later. You can start Stripe KYC any time from the Payments page.
                </div>
                <a className="btn s" href="/stripe-setup" target="_blank" rel="noreferrer" data-testid="welcome-open-stripe">
                  Open Payments page in a new tab
                </a>
              </>
            )}

            {active === 'barber' && (
              <>
                <h3 style={{ marginTop: 0 }}>Add your first barber</h3>
                <div className="sub" style={{ color: 'var(--mute)', margin: '4px 0 14px' }}>
                  Optional. Add just the name for now — bios, hours and photos can be set from the Barbers page later.
                </div>

                <div className="field" style={{ background: 'var(--paper, #fafafa)', border: '1px solid var(--line)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={ownAsBarber}
                      onChange={(e) => setOwnAsBarber(e.target.checked)}
                      data-testid="welcome-own-as-barber"
                    />
                    <span><b>Add me as a barber too</b> — {ownerName || ownerEmail || 'me'}</span>
                  </label>
                  <div style={{ color: 'var(--mute)', fontSize: 12, marginTop: 6, paddingLeft: 24 }}>
                    Most solo-chair shops cut hair themselves. Untick if you only manage the shop.
                  </div>
                </div>
                {(shop.plan || 'standard').toLowerCase() === 'standard' && (
                  <div
                    data-testid="welcome-barber-plan-nudge"
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--paper)',
                      borderRadius: 13,
                      padding: '12px 14px',
                      margin: '0 0 16px',
                      fontSize: 13,
                      color: 'var(--ink)',
                    }}
                  >
                    <b>Standard plan · 1 barber</b>
                    <div style={{ color: 'var(--mute)', marginTop: 2 }}>
                      Need more chairs?{' '}
                      <button
                        type="button"
                        className="lnk"
                        onClick={() => navigate('/billing')}
                        data-testid="welcome-barber-upgrade-link"
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--acc)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                      >
                        Upgrade to Premium
                      </button>{' '}
                      to add up to 3 barbers.
                    </div>
                  </div>
                )}
                <div className="wiz-row">
                  <div className="field">
                    <label>Name</label>
                    <input className="inp" value={barberName} onChange={(e) => setBarberName(e.target.value)} data-testid="welcome-barber-name" />
                  </div>
                  <div className="field">
                    <label>Role <small style={{ color: 'var(--mute)' }}>optional</small></label>
                    <select
                      className="inp"
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setBarberRole(barberRoleLabel(e.target.value));
                      }}
                      style={{ marginBottom: 8 }}
                      data-testid="welcome-barber-role-template"
                      aria-label="Pick a default barber role"
                    >
                      <option value="">Use a default role…</option>
                      {barberRoleOptions(shop.plan).map(o => (
                        <option key={o.id} value={o.id} disabled={o.disabled}>
                          {o.label}{o.hint}
                        </option>
                      ))}
                    </select>
                    <input className="inp" value={barberRole} onChange={(e) => setBarberRole(e.target.value)} placeholder="Master barber" data-testid="welcome-barber-role" />
                  </div>
                </div>
              </>
            )}

            {active === 'done' && (
              <>
                <h3 style={{ marginTop: 0 }}>You&apos;re all set</h3>
                <div className="sub" style={{ color: 'var(--mute)', margin: '4px 0 14px' }}>
                  Head to your dashboard to see today&apos;s bookings and manage the shop. You can always come back to complete Payments and add more barbers from the sidebar.
                </div>
                <div className="cred-box">
                  <b>Booking URL:</b> {shop.subdomain}.instacut.co.uk
                </div>
              </>
            )}

            {error && <div className="modal-error" style={{ marginTop: 12 }} data-testid="welcome-error">{error}</div>}

            <div className="wiz-foot">
              <button className="btn s" onClick={back} disabled={step === 0 || saving} data-testid="welcome-back">
                <ChevronLeft size={14} /> Back
              </button>
              <div className="rside">
                {active !== 'done' && !STEPS[step].required && (
                  <button className="btn s" onClick={skip} disabled={saving} data-testid="welcome-skip">
                    Skip for now
                  </button>
                )}
                {active !== 'done' && (
                  <button
                    className="btn p"
                    onClick={next}
                    disabled={saving || (active === 'profile' && !profileValid)}
                    data-testid="welcome-next"
                  >
                    {saving ? 'Saving…' : (<>Next <ChevronRight size={14} /></>)}
                  </button>
                )}
                {active === 'done' && (
                  <button className="btn p" onClick={finish} disabled={saving} data-testid="welcome-finish">
                    {saving ? 'Finishing…' : 'Go to dashboard'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

const SubStatus = ({ status }) => {
  if (status === 'idle')     return null;
  if (status === 'checking') return <small style={{ color: 'var(--mute)' }}>Checking…</small>;
  if (status === 'invalid')  return <small style={{ color: '#b03a2e' }}>Must be 2–30 chars, lowercase letters, numbers or dashes.</small>;
  if (status === 'taken')    return <small style={{ color: '#b03a2e' }}>Already taken.</small>;
  if (status === 'ok')       return <small style={{ color: '#059669' }}>Available</small>;
  return null;
};

const ServicesStep = ({ services, setServices }) => {
  const upd = (i, patch) => setServices(services.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const rm  = (i) => setServices(services.filter((_, idx) => idx !== i));
  const add = () => setServices([...services, emptyService()]);
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Services</h3>
        <button className="btn s" onClick={add} data-testid="welcome-services-add"><Plus size={14} /> Add service</button>
      </div>
      <div className="sub" style={{ color: 'var(--mute)', margin: '4px 0 14px' }}>Add a few now, or skip and add them from the Services page later.</div>
      {services.length === 0 && <div style={{ color: 'var(--mute2)', padding: '16px 0' }}>No services yet.</div>}
      {services.map((s, i) => (
        <div key={i} className="rep-card" data-testid={`welcome-service-${i}`}>
          <div className="rep-card-h">
            <b>Service #{i + 1}</b>
            <button className="btn s" onClick={() => rm(i)} data-testid={`welcome-service-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          <div className="wiz-row three">
            <div className="field"><label>Name</label>
              <input className="inp" value={s.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`welcome-service-name-${i}`} />
            </div>
            <div className="field"><label>Duration (min)</label>
              <input className="inp" type="number" min="5" step="5" value={s.duration_min} onChange={(e) => upd(i, { duration_min: e.target.value })} data-testid={`welcome-service-duration-${i}`} />
            </div>
            <div className="field"><label>Price (£)</label>
              <input className="inp" type="number" min="0" step="0.5" value={s.price} onChange={(e) => upd(i, { price: e.target.value })} data-testid={`welcome-service-price-${i}`} />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const Welcome = () => <AppShell><WelcomeInner /></AppShell>;
export default Welcome;
